/**
 * Copyright (c) 2025 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { CHAT_GROUP_MAX_MEMBERS } from '../chat/constants'
import { UserRole } from './roles'

// ============================================================================
// FEATURE FLAG TYPES
// ============================================================================

/** Feature flag keys for type-safe access */
export const FeatureFlag = {
  // Album limits
  ALBUM_CREATION_LIMIT: 'albumCreationLimit',
  // Media limits
  MEDIA_UPLOAD_LIMIT: 'mediaUploadLimit',
  IMAGE_EDITOR_FREE_USES: 'imageEditorFreeUses',
  // Post limits
  POST_MAX_MEDIA: 'postMaxMedia',
  POST_MAX_PAGES: 'postMaxPages',
  // Vendor market limits
  VENDOR_MARKET_CREATE_LIMIT: 'vendorMarketCreateLimit',
  VENDOR_MARKET_IMAGE_LIMIT: 'vendorMarketImageLimit',
  VENDOR_MARKET_ITEM_IMAGE_LIMIT: 'vendorMarketItemImageLimit',
  VENDOR_MARKET_ITEM_LIMIT: 'vendorMarketItemLimit',
  // Chat limits
  CHAT_GROUP_MAX_MEMBERS: 'chatGroupMaxMembers',
  // Feature toggles
  SUPPORTER_PAGE_ENABLED: 'supporterPageEnabled',
} as const

export type FeatureFlagKey = (typeof FeatureFlag)[keyof typeof FeatureFlag]

/** Feature flags are role-based limits and toggles (not permissions) */
export interface FeatureFlags {
  /** Maximum number of albums a user can create */
  [FeatureFlag.ALBUM_CREATION_LIMIT]: number
  /** Maximum number of media items a user can upload */
  [FeatureFlag.MEDIA_UPLOAD_LIMIT]: number
  /**
   * Free image-editor applies before `media:edit-image` (preview+) is required.
   * Roles with that permission are unlimited and do not consume this quota.
   */
  [FeatureFlag.IMAGE_EDITOR_FREE_USES]: number
  /** Maximum number of media items per post */
  [FeatureFlag.POST_MAX_MEDIA]: number
  /** Maximum number of pages/posts visible in feed */
  [FeatureFlag.POST_MAX_PAGES]: number
  /** Whether the user needs to see the supporter upgrade page */
  [FeatureFlag.SUPPORTER_PAGE_ENABLED]: boolean
  /** Maximum number of vendor market listings a user can create */
  [FeatureFlag.VENDOR_MARKET_CREATE_LIMIT]: number
  /**
   * Max images a viewer may see on another user's non-supporter vendor market listing (permission id:
   * vendor-market:image-limit). Listing owner and staff with `vendor-market:review` bypass this in the
   * resolver; use `Number.MAX_VALUE` only if you intentionally remove the cap for a role.
   */
  [FeatureFlag.VENDOR_MARKET_IMAGE_LIMIT]: number
  /**
   * Max item media a viewer may see when the vendor market owner is not supporter+ (permission id:
   * vendor-market:item-image-limit). Item owner and staff with `vendor-market:review` bypass in the resolver.
   */
  [FeatureFlag.VENDOR_MARKET_ITEM_IMAGE_LIMIT]: number
  /** Maximum number of items a user can add to a vendor market listing */
  [FeatureFlag.VENDOR_MARKET_ITEM_LIMIT]: number
  /**
   * Maximum active members in a GROUP chat the caller may create or invite into.
   * Channels are not capped by this flag. Includes the caller.
   */
  [FeatureFlag.CHAT_GROUP_MAX_MEMBERS]: number
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  [FeatureFlag.ALBUM_CREATION_LIMIT]: 10,
  [FeatureFlag.MEDIA_UPLOAD_LIMIT]: 36,
  [FeatureFlag.IMAGE_EDITOR_FREE_USES]: 2,
  [FeatureFlag.POST_MAX_MEDIA]: 10,
  [FeatureFlag.POST_MAX_PAGES]: Number.POSITIVE_INFINITY,
  [FeatureFlag.VENDOR_MARKET_CREATE_LIMIT]: Number.POSITIVE_INFINITY,
  [FeatureFlag.VENDOR_MARKET_IMAGE_LIMIT]: Number.POSITIVE_INFINITY,
  [FeatureFlag.VENDOR_MARKET_ITEM_IMAGE_LIMIT]: Number.POSITIVE_INFINITY,
  [FeatureFlag.VENDOR_MARKET_ITEM_LIMIT]: Number.POSITIVE_INFINITY,
  [FeatureFlag.CHAT_GROUP_MAX_MEMBERS]: CHAT_GROUP_MAX_MEMBERS,
  [FeatureFlag.SUPPORTER_PAGE_ENABLED]: false,
}

// ============================================================================
// ROLE FEATURE FLAG CONFIGURATION
// ============================================================================

export const ROLE_FEATURE_FLAGS: Record<UserRole, FeatureFlags> = {
  [UserRole.UNAUTH]: {
    [FeatureFlag.ALBUM_CREATION_LIMIT]: 0,
    [FeatureFlag.MEDIA_UPLOAD_LIMIT]: 0,
    [FeatureFlag.IMAGE_EDITOR_FREE_USES]: 0,
    [FeatureFlag.POST_MAX_MEDIA]: 0,
    [FeatureFlag.POST_MAX_PAGES]: 0,
    [FeatureFlag.VENDOR_MARKET_CREATE_LIMIT]: 0,
    [FeatureFlag.VENDOR_MARKET_IMAGE_LIMIT]: 0,
    [FeatureFlag.VENDOR_MARKET_ITEM_IMAGE_LIMIT]: 0,
    [FeatureFlag.VENDOR_MARKET_ITEM_LIMIT]: 0,
    [FeatureFlag.CHAT_GROUP_MAX_MEMBERS]: 0,
    [FeatureFlag.SUPPORTER_PAGE_ENABLED]: true,
  },
  [UserRole.MEMBER]: {
    [FeatureFlag.ALBUM_CREATION_LIMIT]: 2,
    [FeatureFlag.MEDIA_UPLOAD_LIMIT]: 1,
    [FeatureFlag.IMAGE_EDITOR_FREE_USES]: 2,
    [FeatureFlag.POST_MAX_MEDIA]: 1,
    [FeatureFlag.POST_MAX_PAGES]: 2,
    [FeatureFlag.VENDOR_MARKET_CREATE_LIMIT]: 1,
    [FeatureFlag.VENDOR_MARKET_IMAGE_LIMIT]: 2,
    [FeatureFlag.VENDOR_MARKET_ITEM_IMAGE_LIMIT]: 1,
    [FeatureFlag.VENDOR_MARKET_ITEM_LIMIT]: 2,
    [FeatureFlag.CHAT_GROUP_MAX_MEMBERS]: 4,
    [FeatureFlag.SUPPORTER_PAGE_ENABLED]: true,
  },
  [UserRole.PREVIEW]: {
    ...DEFAULT_FEATURE_FLAGS,
    [FeatureFlag.ALBUM_CREATION_LIMIT]: 3,
    [FeatureFlag.SUPPORTER_PAGE_ENABLED]: true,
    [FeatureFlag.VENDOR_MARKET_CREATE_LIMIT]: 1,
    [FeatureFlag.VENDOR_MARKET_IMAGE_LIMIT]: 2,
    [FeatureFlag.VENDOR_MARKET_ITEM_IMAGE_LIMIT]: 1,
    [FeatureFlag.VENDOR_MARKET_ITEM_LIMIT]: 2,
    [FeatureFlag.CHAT_GROUP_MAX_MEMBERS]: 4,
  },
  [UserRole.SUPPORTER]: {
    ...DEFAULT_FEATURE_FLAGS,
  },
  [UserRole.TRADESHOW]: {
    ...DEFAULT_FEATURE_FLAGS,
  },
  [UserRole.MODERATOR]: {
    ...DEFAULT_FEATURE_FLAGS,
  },
  [UserRole.ADMIN]: {
    ...DEFAULT_FEATURE_FLAGS,
  },
}

// ============================================================================
// FEATURE FLAG UTILITIES
// ============================================================================

/** Get all feature flags for a role */
export function getFeatureFlags(role: UserRole): FeatureFlags {
  return ROLE_FEATURE_FLAGS[role]
}

/** Get a specific feature flag value for a role */
export function getFeatureFlag<K extends keyof FeatureFlags>(role: UserRole, flag: K): FeatureFlags[K] {
  return ROLE_FEATURE_FLAGS[role ?? UserRole.UNAUTH][flag]
}
